import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash2 } from "lucide-react";
import { apiService, getApiErrorMessage } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeUserRole } from "@/lib/roles";

const PREFERENCES_KEY = "payroll_preferences";

type Preferences = {
  darkMode: boolean;
  compactView: boolean;
  language: string;
};

const defaultPreferences: Preferences = {
  darkMode: false,
  compactView: false,
  language: "en",
};

function readPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return { ...defaultPreferences };
    const parsed = JSON.parse(raw);
    return {
      darkMode: Boolean(parsed?.darkMode),
      compactView: Boolean(parsed?.compactView),
      language: typeof parsed?.language === "string" ? parsed.language : "en",
    };
  } catch {
    return { ...defaultPreferences };
  }
}

function applyPreferencesToDom(prefs: Preferences) {
  document.documentElement.classList.toggle("dark", prefs.darkMode);
  document.body.classList.toggle("compact-view", prefs.compactView);
}

const Settings = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const [darkMode, setDarkMode] = useState(false);
  const [compactView, setCompactView] = useState(false);
  const [language, setLanguage] = useState("en");

  // State for departments and job positions
  const [departmentList, setDepartmentList] = useState([]);
  const [positionList, setPositionList] = useState([]);
  const [newDepartment, setNewDepartment] = useState("");
  const [newPosition, setNewPosition] = useState("");
  const [newPositionDept, setNewPositionDept] = useState("");

  const [allowances, setAllowances] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [salaryScales, setSalaryScales] = useState([]);
  const [editingScale, setEditingScale] = useState<any | null>(null);
  const [editScaleForm, setEditScaleForm] = useState({
    scale_name: "",
    min_amount: "",
    max_amount: "",
    leave_days: "",
  });
  const [savingScale, setSavingScale] = useState(false);

  const [banks, setBanks] = useState([]);
  const [newBankName, setNewBankName] = useState("");
  const [newBankCode, setNewBankCode] = useState("");
  const [addingBank, setAddingBank] = useState(false);

  useEffect(() => {
    const prefs = readPreferences();
    setDarkMode(prefs.darkMode);
    setCompactView(prefs.compactView);
    setLanguage(prefs.language);
    applyPreferencesToDom(prefs);
  }, []);

  const load = async () => {
    const cid = user?.cid ?? null;
    const allowancesRes = await apiService.getAllowances(cid);
    const deductionsRes = await apiService.getDeductions(cid);
    const salaryScalesRes = await apiService.getSalaryScales(cid);

    setAllowances(allowancesRes.data);
    setDeductions(deductionsRes.data);
    setSalaryScales(salaryScalesRes.data);
  };

  const loadBanks = async () => {
    try {
      const res = await apiService.getBanks(user?.cid ?? null);
      setBanks(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to load banks."),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    load();
    loadBanks();
  }, [user?.cid]);

  const handleToggleAllowancePaye = async (allowance: any, checked: boolean) => {
    try {
      await apiService.updateAllowance(String(allowance.id), {
        is_mandatory: checked,
        role: normalizeUserRole(user?.role),
      });
      await load();
      toast({
        title: checked ? "PAYE enabled" : "PAYE removed",
        description: checked
          ? `${allowance.name} will be included in taxable income on the next payroll run.`
          : `${allowance.name} is now ZRA exempt. Rerun payroll for open periods to refresh PAYE.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to update allowance PAYE setting."),
        variant: "destructive",
      });
    }
  };

  const fetchDepartments = () =>
    apiService.getDepartments(user?.cid ?? null).then(data =>
      setDepartmentList(Array.isArray(data.data) ? data.data : [])
    );

  const fetchPositions = () =>
    apiService.getPositions(user?.cid ?? null).then(data =>
      setPositionList(Array.isArray(data.data) ? data.data : [])
    );

  useEffect(() => {
    fetchDepartments();
    fetchPositions();
  }, []);

  const handleSave = () => {
    setSaving(true);
    try {
      const prefs: Preferences = { darkMode, compactView, language };
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
      applyPreferencesToDom(prefs);
      toast({
        title: "Settings saved",
        description: "Your preferences have been saved.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to save preferences.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDarkModeChange = (checked: boolean) => {
    setDarkMode(checked);
    document.documentElement.classList.toggle("dark", checked);
  };

  const handleCompactViewChange = (checked: boolean) => {
    setCompactView(checked);
    document.body.classList.toggle("compact-view", checked);
  };

  const handleAddDepartment = async () => {
    if (newDepartment.trim() === "") {
      toast({ title: "Error", description: "Department name cannot be empty.", variant: "destructive" });
      return;
    }
    try {
      await apiService.createDepartment({ name: newDepartment, cid: user?.cid ?? null });
      setNewDepartment("");
      fetchDepartments();
      toast({ title: "Success", description: "Department added successfully." });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to add department."),
        variant: "destructive",
      });
    }
  };

  const handleAddPosition = async () => {
    if (newPosition.trim() === "" || !newPositionDept) {
      toast({ title: "Error", description: "Position name and department are required.", variant: "destructive" });
      return;
    }
    try {
      await apiService.createPosition({ title: newPosition, department_id: Number(newPositionDept) });
      setNewPosition("");
      setNewPositionDept("");
      fetchPositions();
      toast({ title: "Success", description: "Position added successfully." });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to add position."),
        variant: "destructive",
      });
    }
  };

  const handleRemoveDepartment = (index: number) => {
    const updatedDepartments = departmentList.filter((_, i) => i !== index);
    setDepartmentList(updatedDepartments);
    toast({ title: "Success", description: "Department removed successfully." });
  };

  const handleRemovePosition = (index: number) => {
    const updatedPositions = positionList.filter((_, i) => i !== index);
    setPositionList(updatedPositions);
    toast({ title: "Success", description: "Position removed successfully." });
  };

  const openEditScale = (scale: any) => {
    setEditingScale(scale);
    setEditScaleForm({
      scale_name: scale.scale_name ?? "",
      min_amount: String(scale.min_amount ?? ""),
      max_amount: String(scale.max_amount ?? ""),
      leave_days: String(scale.leave_days ?? 0),
    });
  };

  const handleUpdateSalaryScale = async () => {
    if (!editingScale) return;
    setSavingScale(true);
    try {
      await apiService.updateSalaryScale(String(editingScale.id), {
        scale_name: editScaleForm.scale_name,
        min_amount: parseFloat(editScaleForm.min_amount),
        max_amount: parseFloat(editScaleForm.max_amount),
        leave_days: parseFloat(editScaleForm.leave_days) || 0,
        role: normalizeUserRole(user?.role),
      });
      setEditingScale(null);
      await load();
      toast({ title: "Success", description: "Salary scale updated successfully." });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to update salary scale."),
        variant: "destructive",
      });
    } finally {
      setSavingScale(false);
    }
  };

  const handleDeleteSalaryScale = async (scale: any) => {
    if (!confirm("Are you sure you want to delete this salary scale?")) return;
    try {
      await apiService.deleteSalaryScale(scale.id);
      await load();
      toast({ title: "Success", description: "Salary scale deleted successfully." });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to delete salary scale."),
        variant: "destructive",
      });
    }
  };

  const handleAddBank = async () => {
    if (newBankName.trim() === "") {
      toast({ title: "Error", description: "Bank name is required.", variant: "destructive" });
      return;
    }
    setAddingBank(true);
    try {
      await apiService.createBank({
        name: newBankName.trim(),
        code: newBankCode.trim() || undefined,
        cid: user?.cid ?? null,
      });
      setNewBankName("");
      setNewBankCode("");
      await loadBanks();
      toast({ title: "Success", description: "Bank added successfully." });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to add bank."),
        variant: "destructive",
      });
    } finally {
      setAddingBank(false);
    }
  };

  const handleToggleBankActive = async (bank: any, checked: boolean) => {
    try {
      await apiService.updateBank({
        id: bank.id,
        is_active: checked,
        role: normalizeUserRole(user?.role),
      });
      await loadBanks();
      toast({
        title: "Success",
        description: checked ? `${bank.name} enabled.` : `${bank.name} disabled.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to update bank."),
        variant: "destructive",
      });
    }
  };

  const handleDeleteBank = async (bank: any) => {
    if (!confirm(`Delete bank "${bank.name}"?`)) return;
    try {
      await apiService.deleteBank({
        id: bank.id,
        role: normalizeUserRole(user?.role),
      });
      await loadBanks();
      toast({ title: "Success", description: "Bank deleted successfully." });
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to delete bank."),
        variant: "destructive",
      });
    }
  };

  // AddAllowanceForm component
  function AddAllowanceForm({ onSuccess }: { onSuccess: () => void }) {
    const [form, setForm] = useState({ name: '', type: 'fixed', percentage_base: 'basic', value: '', is_mandatory: false });
    const [loading, setLoading] = useState(false);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm({ ...form, [e.target.name]: e.target.value });
    };
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm({ ...form, [e.target.name]: e.target.checked });
    };
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
        const payload: any = { ...form, value: parseFloat(form.value) };
        if (form.type === 'fixed') delete payload.percentage_base;
        payload.cid = user?.cid ?? null;
        await apiService.createAllowance(payload);
        setForm({ name: '', type: 'fixed', percentage_base: 'basic', value: '', is_mandatory: false });
        toast({ title: "Success", description: "Allowance added successfully." });
        onSuccess();
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error, "Failed to add allowance."),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 mb-4">
        <div className="flex gap-2">
          <Input name="name" placeholder="Allowance Name" value={form.name} onChange={handleChange} required />
          <select name="type" value={form.type} onChange={handleChange} className="border rounded p-2">
            <option value="fixed">Fixed</option>
            <option value="percentage">Percentage</option>
          </select>
          {form.type === 'percentage' && (
            <select name="percentage_base" value={form.percentage_base} onChange={handleChange} className="border rounded p-2">
              <option value="basic">Basic Pay</option>
              <option value="net">Net Salary</option>
              <option value="gross">Gross Salary</option>
            </select>
          )}
          <Input name="value" type="number" step="0.01" placeholder="Value" value={form.value} onChange={handleChange} required />
          <div className="flex items-center gap-2" title="Include this allowance in PAYE taxable income">
            <input
              type="checkbox"
              name="is_mandatory"
              checked={form.is_mandatory}
              onChange={handleCheckboxChange}
              className="rounded"
            />
            <label className="text-sm">PAYE</label>
          </div>
          <Button type="submit" disabled={loading}>{loading ? 'Adding...' : 'Add'}</Button>
        </div>
      </form>
    );
  }

  // AddDeductionForm component
  function AddDeductionForm({ onSuccess }: { onSuccess: () => void }) {
    const [form, setForm] = useState({
      name: '',
      type: 'fixed',
      percentage_base: 'basic',
      value: '',
      employer_value: '',
      is_mandatory: false,
    });
    const [loading, setLoading] = useState(false);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm({ ...form, [e.target.name]: e.target.value });
    };
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm({ ...form, [e.target.name]: e.target.checked });
    };
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
        const payload: any = {
          ...form,
          value: parseFloat(form.value),
          employer_value: parseFloat(form.employer_value) || 0,
        };
        if (form.type === 'fixed') delete payload.percentage_base;
        payload.cid = user?.cid ?? null;
        await apiService.createDeduction(payload);
        setForm({
          name: '',
          type: 'fixed',
          percentage_base: 'basic',
          value: '',
          employer_value: '',
          is_mandatory: false,
        });
        toast({ title: "Success", description: "Deduction added successfully." });
        onSuccess();
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error, "Failed to add deduction."),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 mb-4">
        <div className="flex flex-wrap gap-2">
          <Input name="name" placeholder="Deduction Name" value={form.name} onChange={handleChange} required />
          <select name="type" value={form.type} onChange={handleChange} className="border rounded p-2">
            <option value="fixed">Fixed</option>
            <option value="percentage">Percentage</option>
          </select>
          {form.type === 'percentage' && (
            <select name="percentage_base" value={form.percentage_base} onChange={handleChange} className="border rounded p-2">
              <option value="basic">Basic Pay</option>
              <option value="net">Net Salary</option>
              <option value="gross">Gross Salary</option>
            </select>
          )}
          <Input
            name="value"
            type="number"
            step="0.01"
            placeholder={form.type === 'percentage' ? 'Employee %' : 'Employee amount'}
            value={form.value}
            onChange={handleChange}
            required
          />
          <Input
            name="employer_value"
            type="number"
            step="0.01"
            min="0"
            placeholder={form.type === 'percentage' ? 'Employer % (optional)' : 'Employer amount (optional)'}
            value={form.employer_value}
            onChange={handleChange}
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="is_mandatory"
              checked={form.is_mandatory}
              onChange={handleCheckboxChange}
              className="rounded"
            />
            <label className="text-sm">Mandatory</label>
          </div>
          <Button type="submit" disabled={loading}>{loading ? 'Adding...' : 'Add'}</Button>
        </div>
      </form>
    );
  }

  // AddSalaryScaleForm component
  function AddSalaryScaleForm({ onSuccess }: { onSuccess: () => void }) {
    const { user } = useAuth();
    const [form, setForm] = useState({ scale_name: '', min_amount: '', max_amount: '', leave_days: '' });
    const [loading, setLoading] = useState(false);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm({ ...form, [e.target.name]: e.target.value });
    };
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
        const payload: any = {
          ...form,
          min_amount: parseFloat(form.min_amount),
          max_amount: parseFloat(form.max_amount),
          leave_days: parseFloat(form.leave_days) || 0,
        };
        payload.cid = user?.cid ?? null;
        await apiService.createSalaryScale(payload);
        setForm({ scale_name: '', min_amount: '', max_amount: '', leave_days: '' });
        toast({ title: "Success", description: "Salary scale added successfully." });
        onSuccess();
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error, "Failed to add salary scale."),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 mb-4">
        <div className="flex gap-2">
          <Input name="scale_name" placeholder="Scale Name (e.g., LGSS 1)" value={form.scale_name} onChange={handleChange} required />
          <Input name="min_amount" type="number" step="0.01" placeholder="Min Amount" value={form.min_amount} onChange={handleChange} required />
          <Input name="max_amount" type="number" step="0.01" placeholder="Max Amount" value={form.max_amount} onChange={handleChange} required />
          <Input name="leave_days" type="number" min="0" max="31" step="0.01" placeholder="Leave Days / Month" value={form.leave_days} onChange={handleChange} required />
          <Button type="submit" disabled={loading}>{loading ? 'Adding...' : 'Add'}</Button>
        </div>
      </form>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-gray-500">Manage your account settings and preferences</p>
        </div>

        <Tabs defaultValue="general">
          <TabsList className="mb-6">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="departments">Departments & Positions</TabsTrigger>
            <TabsTrigger value="allowances-deductions">Allowances & Deductions</TabsTrigger>
            <TabsTrigger value="salary-scales">Salary Scales</TabsTrigger>
            <TabsTrigger value="banks">Banks</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Manage your basic account settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Personal Information</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        placeholder="Username"
                        value={user?.username || ''}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        placeholder="Email"
                        value={user?.email || ''}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Input
                        id="role"
                        placeholder="Role"
                        value={user?.role || ''}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Account Information</h3>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-lg">
                          {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {user?.username || 'Unknown User'}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {user?.email || 'No email provided'}
                        </p>
                        <p className="text-xs text-blue-600 font-medium">
                          {user?.role || 'No role assigned'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Preferences</h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Dark Mode</p>
                        <p className="text-sm text-gray-500">Enable dark mode for the application</p>
                      </div>
                      <Switch
                        id="darkMode"
                        checked={darkMode}
                        onCheckedChange={handleDarkModeChange}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Compact View</p>
                        <p className="text-sm text-gray-500">Use compact layout for tables and lists</p>
                      </div>
                      <Switch
                        id="compactView"
                        checked={compactView}
                        onCheckedChange={handleCompactViewChange}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="language">Language</Label>
                      <select
                        id="language"
                        className="w-full rounded-md border border-gray-300 p-2"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    className="bg-payroll-primary hover:bg-blue-700"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Departments & Positions Tab */}
          <TabsContent value="departments">
            <Card>
              <CardHeader>
                <CardTitle>Departments & Job Positions</CardTitle>
                <CardDescription>
                  Manage the list of departments and job positions available in your organization.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Departments Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Departments</h3>
                  <div className="flex items-center gap-4">
                    <Input
                      placeholder="Add a new department"
                      value={newDepartment}
                      onChange={(e) => setNewDepartment(e.target.value)}
                    />
                    <Button onClick={handleAddDepartment}>Add</Button>
                  </div>
                  <ul className="space-y-2">
                    {(Array.isArray(departmentList) ? departmentList : []).map(dep => (
                      <li key={dep.id} className="flex items-center justify-between border p-2 rounded">
                        <span>{dep.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveDepartment(departmentList.findIndex(d => d.id === dep.id))}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>

                <Separator />

                {/* Job Positions Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Job Positions</h3>
                  <div className="flex items-center gap-4">
                    <Input
                      placeholder="Add a new job position"
                      value={newPosition}
                      onChange={(e) => setNewPosition(e.target.value)}
                    />
                    <select
                      value={newPositionDept}
                      onChange={e => setNewPositionDept(e.target.value)}
                      className="border rounded p-2"
                    >
                      <option value="">Select Department</option>
                      {departmentList?.map(dep => (
                        <option key={dep.id} value={dep.id}>{dep.name}</option>
                      ))}
                    </select>
                    <Button onClick={handleAddPosition}>Add</Button>
                  </div>
                  <ul className="space-y-2">
                    {(Array.isArray(positionList) ? positionList : []).map(position => (
                      <li key={position.id} className="flex items-center justify-between border p-2 rounded">
                        <span>{position.title} ({departmentList.find(d => d.id === position.department_id)?.name || ""})</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemovePosition(positionList.findIndex(p => p.id === position.id))}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="allowances-deductions">
            <Card>
              <CardHeader>
                <CardTitle>Allowances & Deductions</CardTitle>
                <CardDescription>Manage Allowances and Deductions used in payroll calculation.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Allowances</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Tick PAYE on an allowance to include it in taxable income when payroll calculates PAYE.
                  </p>
                  <AddAllowanceForm onSuccess={load} />
                  <ul className="space-y-2">
                    {allowances.map((a: any) => (
                      <li key={a.id} className="flex items-center justify-between border p-2 rounded gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <span>{a.name} ({a.type === 'fixed' ? 'ZMW ' + a.value : a.value + '% of ' + a.percentage_base})</span>
                          {a.is_mandatory && (
                            <span className="bg-amber-100 text-amber-900 text-xs px-2 py-1 rounded">PAYE</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Label htmlFor={`allowance-paye-${a.id}`} className="text-xs text-muted-foreground">
                            PAYE
                          </Label>
                          <Switch
                            id={`allowance-paye-${a.id}`}
                            checked={!!a.is_mandatory}
                            onCheckedChange={(checked) => handleToggleAllowancePaye(a, checked)}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-2">Deductions</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Employee value is deducted from pay. Employer value (optional) uses the same % base and appears on remittance reports only (NAPSA/NHIMA still mirror employee = employer).
                  </p>
                  <AddDeductionForm onSuccess={load} />
                  <ul className="space-y-2">
                    {deductions.map((d: any) => (
                      <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 border p-2 rounded">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>
                            {d.name}{' '}
                            ({d.type === 'fixed' ? 'ZMW ' + d.value : d.value + '% of ' + d.percentage_base}
                            {Number(d.employer_value) > 0
                              ? ` · Employer ${d.type === 'fixed' ? 'ZMW ' + d.employer_value : d.employer_value + '%'}`
                              : ''}
                            )
                          </span>
                          {d.is_mandatory && (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Mandatory</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            className="w-28"
                            type="number"
                            step="0.01"
                            min="0"
                            defaultValue={d.employer_value ?? 0}
                            placeholder="Employer"
                            aria-label={`Employer value for ${d.name}`}
                            onBlur={async (e) => {
                              const next = parseFloat(e.target.value);
                              if (Number.isNaN(next) || next === Number(d.employer_value ?? 0)) return;
                              try {
                                await apiService.updateDeduction(String(d.id), {
                                  employer_value: next,
                                  role: normalizeUserRole(user?.role),
                                });
                                toast({ title: 'Saved', description: `Employer contribution updated for ${d.name}.` });
                                load();
                              } catch (error) {
                                toast({
                                  title: 'Error',
                                  description: getApiErrorMessage(error, 'Failed to update employer contribution.'),
                                  variant: 'destructive',
                                });
                              }
                            }}
                          />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {d.type === 'percentage' ? 'Employer %' : 'Employer ZMW'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="salary-scales">
            <Card>
              <CardHeader>
                <CardTitle>Salary Scales</CardTitle>
                <CardDescription>Manage salary scales used for employee salary ranges.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Add New Salary Scale</h3>
                  <AddSalaryScaleForm onSuccess={load} />
                  <ul className="space-y-2">
                    {salaryScales.map((scale: any) => (
                      <li key={scale.id} className="flex items-center justify-between border p-2 rounded">
                        <div className="flex items-center gap-4">
                          <span className="font-medium">{scale.scale_name}</span>
                          <span className="text-sm text-gray-600">
                            ZMW {Number(scale.min_amount).toLocaleString()} - ZMW {Number(scale.max_amount).toLocaleString()}
                          </span>
                          <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {scale.leave_days || 0} days/month
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditScale(scale)}
                          >
                            <Pencil className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteSalaryScale(scale)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="banks">
            <Card>
              <CardHeader>
                <CardTitle>Banks</CardTitle>
                <CardDescription>
                  Manage the bank catalog used when assigning employee bank details.
                  Employee records store the bank name (not ID).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Add Bank</h3>
                  <div className="flex items-center gap-4">
                    <Input
                      placeholder="Bank name (required)"
                      value={newBankName}
                      onChange={(e) => setNewBankName(e.target.value)}
                    />
                    <Input
                      placeholder="Code (optional)"
                      value={newBankCode}
                      onChange={(e) => setNewBankCode(e.target.value)}
                    />
                    <Button onClick={handleAddBank} disabled={addingBank}>
                      {addingBank ? "Adding..." : "Add"}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Employee records store the bank name (not ID).
                  </p>
                  <ul className="space-y-2">
                    {(Array.isArray(banks) ? banks : []).map((bank: any) => (
                      <li key={bank.id} className="flex items-center justify-between border p-2 rounded gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-medium">{bank.name}</span>
                          {bank.code && (
                            <span className="text-sm text-gray-500">{bank.code}</span>
                          )}
                          {!bank.is_active && (
                            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">Inactive</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Label htmlFor={`bank-active-${bank.id}`} className="text-xs text-muted-foreground">
                            Active
                          </Label>
                          <Switch
                            id={`bank-active-${bank.id}`}
                            checked={!!bank.is_active}
                            onCheckedChange={(checked) => handleToggleBankActive(bank, checked)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteBank(bank)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editingScale} onOpenChange={(open) => !open && setEditingScale(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Salary Scale</DialogTitle>
            <DialogDescription>Update scale name, amount range, and leave days.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-scale-name">Scale Name</Label>
              <Input
                id="edit-scale-name"
                value={editScaleForm.scale_name}
                onChange={(e) => setEditScaleForm({ ...editScaleForm, scale_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-min-amount">Min Amount</Label>
                <Input
                  id="edit-min-amount"
                  type="number"
                  step="0.01"
                  value={editScaleForm.min_amount}
                  onChange={(e) => setEditScaleForm({ ...editScaleForm, min_amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-max-amount">Max Amount</Label>
                <Input
                  id="edit-max-amount"
                  type="number"
                  step="0.01"
                  value={editScaleForm.max_amount}
                  onChange={(e) => setEditScaleForm({ ...editScaleForm, max_amount: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-leave-days">Leave Days / Month</Label>
              <Input
                id="edit-leave-days"
                type="number"
                min="0"
                max="31"
                step="0.01"
                value={editScaleForm.leave_days}
                onChange={(e) => setEditScaleForm({ ...editScaleForm, leave_days: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingScale(null)}>Cancel</Button>
            <Button onClick={handleUpdateSalaryScale} disabled={savingScale}>
              {savingScale ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Settings;
